import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import RoslinBranding from './RoslinBranding';
import ConfirmDialog from './ConfirmDialog';
import NotificationsDropdown from './NotificationsDropdown';
import UserAvatar from './UserAvatar';

interface HeaderProps {
  title?: string;
  showBackButton?: boolean;
  onBackClick?: () => void;
}

const Header: React.FC<HeaderProps> = ({ 
  title = "Roslin Quiz Platform", 
  showBackButton = false, 
  onBackClick 
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAuthenticated, isAdmin, isSupervisor, isLimitedAdmin, isGuestAccess, logout } = useAuth();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const isAdminPage = location.pathname.includes('/admin');
  const canAccessDashboard = isAdmin || isSupervisor || isLimitedAdmin;

  return (
    <header className="header">
      <div className="container">
        <div className="header-content">
          {showBackButton && (
            <button 
              className="back-button" 
              onClick={onBackClick}
              aria-label="Go back"
            >
              ← Back
            </button>
          )}
          <div className="logo">
            <Link to="/" style={{ textDecoration: 'none' }}>
              <RoslinBranding variant="small" showTagline={false} />
            </Link>
          </div>
          <nav className="nav">
            {isAuthenticated ? (
              <>
                {canAccessDashboard && (
                  <>
                    {isAdminPage ? (
                      <>
                        <Link to="/admin" className="nav-link">Dashboard</Link>
                        {isAdmin && <Link to="/admin/reports" className="nav-link">Reports</Link>}
                      </>
                    ) : (
                      <Link to="/admin" className="nav-link">
                        Dashboard
                      </Link>
                    )}
                  </>
                )}
                <div className="user-menu">
                  <NotificationsDropdown 
                    userId={user?.id || null} 
                    userRole={user?.role || 'guest'} 
                  />
                  <div className="user-info">
                    <UserAvatar name={user?.name} picture={user?.picture} className="user-avatar" />
                    <span className="user-name">{user?.name}</span>
                    {isAdmin && <span className="admin-badge">Admin</span>}
                    {isSupervisor && <span className="admin-badge">Supervisor</span>}
                    {isLimitedAdmin && <span className="admin-badge">CES Admin</span>}
                  </div>
                  <button 
                    onClick={() => setShowLogoutConfirm(true)} 
                    className="logout-btn"
                  >
                    <i className="fas fa-sign-out-alt"></i>
                    Logout
                  </button>
                  <ConfirmDialog
                    isOpen={showLogoutConfirm}
                    onClose={() => setShowLogoutConfirm(false)}
                    onConfirm={logout}
                    title="Confirm Logout"
                    message="Are you sure you want to log out?"
                    confirmText="Yes, Logout"
                    cancelText="Cancel"
                  />
                </div>
              </>
            ) : isGuestAccess ? (
              <>
                <NotificationsDropdown 
                  userId={null} 
                  userRole="guest" 
                />
                <div className="header-guest-label">
                  <span className="guest-icon">👤</span>
                  Guest Access
                </div>
              </>
            ) : (
              <Link to="/login" className="btn btn-primary header-login-btn">Login</Link>
            )}
          </nav>
        </div>
      </div>
    </header>
  );
};

export default Header;